// Tenantra Inc. - Professional Billing Page (Microsoft 365 Admin Style)
import { useState, useEffect } from 'react';
import { CreditCard, AlertCircle, CheckCircle, FileText, RefreshCw, Calendar, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getCurrentUser } from '@/services/auth';
import BillingIntervalSelector from '@/components/BillingIntervalModal';
import BillingAddressGate from '@/components/BillingAddressGate';
import SubscriptionSuccessModal from '@/components/SubscriptionSuccessModal';
import EmbeddedCheckoutPanel from '@/components/EmbeddedCheckoutPanel';
import AddCardForm from '@/components/AddCardForm';
import SavedCardsSection from '@/components/SavedCardsSection';
import { TableSkeleton } from '@/components/dashboard/TableKit';
import api from '@/services/api';
import { toast } from 'sonner';
import { formatDate } from '@/utils/date';

const PLAN_CONFIG = {
  none: { label: 'No Plan', color: 'bg-slate-100 text-slate-600' },
  starter: { label: 'Starter', color: 'bg-blue-100 text-blue-700', price: 'From $29/month' },
  professional: { label: 'Professional', color: 'bg-purple-100 text-purple-700', price: 'From $59/month' },
  enterprise: { label: 'Enterprise', color: 'bg-teal-100 text-teal-700', price: 'From $99/month' },
};

const PLAN_CARDS = [
  { key: 'starter', name: 'Starter', tagline: 'For getting started', features: ['Up to 3 properties', 'Unlimited tenant reports', 'Unlimited lookups', 'Full platform access'] },
  { key: 'professional', name: 'Professional', tagline: 'For growing portfolios', popular: true, features: ['Up to 10 properties', 'Team accounts', 'Advanced analytics', 'Priority support'] },
  { key: 'enterprise', name: 'Enterprise', tagline: 'For large operators', features: ['Unlimited properties', 'API access', 'Bulk operations', 'Priority support'] },
];

export default function BillingPage() {
  const user = getCurrentUser();
  const [currentPlan, setCurrentPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accountInfo, setAccountInfo] = useState(null);
  useEffect(() => { api.get('/auth/me').then(r => setAccountInfo(r.data)).catch(() => {}); }, []);
  const [actionLoading, setActionLoading] = useState(false);
  const [showIntervalSelector, setShowIntervalSelector] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successPlan, setSuccessPlan] = useState(null);
  const [showCardForm, setShowCardForm] = useState(false);
  const [savedCards, setSavedCards] = useState([]);
  const [embeddedSecret, setEmbeddedSecret] = useState(null);
  const [embeddedLabel, setEmbeddedLabel] = useState('');

  useEffect(() => {
    handlePaymentReturn();
  }, []);

  const handlePaymentReturn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const sessionId = urlParams.get('session_id');
    
    if (success === 'true' && sessionId) {
      setLoading(true);
      try {
        const response = await api.post('/billing/verify-session', null, {
          params: { session_id: sessionId }
        });
        
        if (response.data.success) {
          // Show professional welcome modal
          setSuccessPlan(response.data.plan);
          setShowSuccessModal(true);
          window.history.replaceState({}, '', '/landlord/billing');
          setTimeout(() => fetchBillingData(), 1000);
        }
      } catch (err) {
        console.error('Failed to verify session:', err);
        fetchBillingData();
      }
    } else {
      fetchBillingData();
    }
  };

  const [pricing, setPricing] = useState(null);
  const [planError, setPlanError] = useState(false);

  const fetchBillingData = async () => {
    setLoading(true);
    try {
      // First, cleanup any duplicate payment methods silently
      await api.post('/billing/payment-methods/cleanup-duplicates').catch(() => {});

      // Current plan is loaded on its own so a failure here can NEVER blank the page
      // into the dangerous "No Plan + re-subscribe" state for an active subscriber.
      let planFailed = false;
      const planRes = await api.get('/billing/current-plan').catch(() => { planFailed = true; return null; });
      setPlanError(planFailed);
      if (planRes) setCurrentPlan(planRes.data);

      const [historyRes, paymentMethodRes, savedCardsRes] = await Promise.all([
        api.get('/billing/payment-history').catch(() => ({ data: [] })),
        api.get('/billing/payment-method').catch(() => ({ data: { has_payment_method: false } })),
        api.get('/billing/payment-methods').catch(() => ({ data: { payment_methods: [] } })),
        api.get('/billing/pricing').then(r => setPricing(r.data.plans)).catch(() => null)
      ]);
      setPaymentHistory(historyRes.data);
      setPaymentMethod(paymentMethodRes.data);
      setSavedCards(savedCardsRes.data.payment_methods || []);
    } catch (err) {
      console.error('Failed to fetch billing data:', err);
      toast.error('Failed to load billing information');
    } finally {
      setLoading(false);
    }
  };

  const monthlyLabel = (plan) => {
    const p = pricing?.[plan]?.monthly;
    if (!p) return { starter: '$29', professional: '$59', enterprise: '$99' }[plan] + '/mo';
    return p.discount_percent > 0 ? `$${p.per_month.toFixed(2)}/mo (−${p.discount_percent}%)` : `$${p.per_month.toFixed(2)}/mo`;
  };

  const landlordInfo = accountInfo?.landlord;
  const needsBillingAddress = !!landlordInfo && !landlordInfo.billing_address_complete;
  const [addressPrompt, setAddressPrompt] = useState(false);

  const requireAddress = () => {
    if (needsBillingAddress) {
      setAddressPrompt(true);
      toast.error('Confirm your billing province first');
      return false;
    }
    return true;
  };

  const handleUpgrade = async (plan) => {
    if (!requireAddress()) return;
    setSelectedPlan(plan);
    setShowIntervalSelector(true);
  };
  
  const handleIntervalConfirm = async (interval, promoCode = null) => {
    setActionLoading(true);
    try {
      const response = await api.post('/billing/subscribe', {
        plan: selectedPlan,
        billing_interval: interval,
        promo_code: promoCode,
        embedded: true,
        return_url: `${window.location.origin}/landlord/billing?success=true&session_id={CHECKOUT_SESSION_ID}`
      });
      if (response.data.client_secret) {
        setEmbeddedLabel(PLAN_CONFIG[selectedPlan]?.label || '');
        setEmbeddedSecret(response.data.client_secret);
        setShowIntervalSelector(false);
      } else if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url; // safety fallback
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
      setShowIntervalSelector(false);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    // Show dialog with two options
    const choice = window.confirm(
      'Choose cancellation option:\n\n' +
      'Click OK to cancel IMMEDIATELY (lose access now, can subscribe to any plan)\n' +
      'Click Cancel to cancel AT PERIOD END (keep access until ' + 
      formatDate(currentPlan?.next_billing_date) + ')'
    );
    
    const cancelImmediately = choice; // OK = true (immediate), Cancel = false (period end)

    setActionLoading(true);
    try {
      await api.post('/billing/cancel-subscription', null, { 
        params: { at_period_end: !cancelImmediately } 
      });
      
      if (cancelImmediately) {
        toast.success('Subscription canceled immediately. You can now subscribe to any plan.');
      } else {
        toast.success('Subscription will cancel on ' + formatDate(currentPlan?.next_billing_date));
      }
      
      setTimeout(() => {
        fetchBillingData();
        window.location.reload(); // Reload to update navigation
      }, 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setActionLoading(false);
    }
  };

  const handleViewInvoice = async (paymentId) => {
    try {
      // Fetch PDF with authentication
      const response = await api.get(`/billing/invoice/${paymentId}`, {
        responseType: 'blob'
      });
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up blob URL after a delay
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      toast.error('Failed to load invoice');
      console.error('Invoice error:', err);
    }
  };

  if (loading) {
    return <TableSkeleton rows={5} spans={[5, 3, 2, 2]} />;
  }

  const planConfig = PLAN_CONFIG[currentPlan?.plan] || PLAN_CONFIG.none;
  const hasActivePlan = currentPlan?.plan && currentPlan.plan !== 'none' && currentPlan.status === 'active';
  const accountNumber = accountInfo?.account_number || user?.account_number || '------';

  // Show embedded checkout (stays inside Tenantra, no redirect to Stripe)
  if (embeddedSecret) {
    return (
      <div className="py-2">
        <EmbeddedCheckoutPanel
          clientSecret={embeddedSecret}
          planLabel={embeddedLabel}
          onBack={() => { setEmbeddedSecret(null); setSelectedPlan(null); }}
        />
      </div>
    );
  }

  // Show interval selector instead of main content
  if (showIntervalSelector && selectedPlan) {
    return (
      <BillingIntervalSelector
        plan={selectedPlan}
        province={landlordInfo?.province}
        onBack={() => setShowIntervalSelector(false)}
        onContinue={handleIntervalConfirm}
        loading={actionLoading}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-lg font-bold text-slate-900 tracking-tight">Billing &amp; Subscription</h1>
        <p className="text-xs text-slate-500">Account ID <span className="font-mono font-semibold text-slate-700">{accountNumber}</span></p>
      </div>

      {needsBillingAddress && (
        <div className={addressPrompt ? 'ring-2 ring-amber-400 rounded-lg' : ''}>
          <BillingAddressGate
            landlord={landlordInfo}
            onSaved={(addr) => { setAccountInfo((a) => ({ ...a, landlord: { ...a.landlord, ...addr, billing_address_complete: true } })); setAddressPrompt(false); }}
          />
        </div>
      )}

      {/* Current plan summary */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-4 flex items-start justify-between gap-3 border-b border-slate-100">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center shrink-0">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-base font-bold text-slate-900 tracking-tight">{planConfig.label}{hasActivePlan ? ' plan' : ''}</h2>
                {hasActivePlan && currentPlan?.status && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-medium capitalize">{currentPlan.status}</span>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-0.5">
                {planError ? 'Subscription status unavailable' : hasActivePlan ? (planConfig.price || '') : 'Choose a plan below to get started'}
              </p>
            </div>
          </div>
          <button onClick={fetchBillingData} className="text-slate-400 hover:text-teal-600 transition-colors shrink-0" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {hasActivePlan && (
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            {currentPlan?.next_billing_date && (
              <div className="px-5 py-4">
                <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">Next billing</p>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-teal-600" />
                  <span className="text-sm text-slate-800 font-medium">{formatDate(currentPlan.next_billing_date)}</span>
                </div>
                <p className="text-[11px] text-slate-400 mt-1">Auto-renews · tax added by province</p>
              </div>
            )}
            <div className="px-5 py-4">
              <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">Property usage</p>
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-teal-600" />
                <span className="text-sm text-slate-800 font-medium">{currentPlan.properties_used} / {currentPlan.property_limit || '∞'}</span>
              </div>
              {currentPlan.property_limit && (
                <div className="w-full bg-slate-100 rounded-full h-1.5 mt-2">
                  <div
                    className={`h-1.5 rounded-full transition-all ${(currentPlan.properties_used / currentPlan.property_limit) >= 1 ? 'bg-red-500' : (currentPlan.properties_used / currentPlan.property_limit) >= 0.8 ? 'bg-amber-500' : 'bg-teal-600'}`}
                    style={{ width: `${Math.min(100, (currentPlan.properties_used / currentPlan.property_limit) * 100)}%` }}
                  />
                </div>
              )}
            </div>
            <div className="px-5 py-4">
              <p className="text-[11px] font-medium text-slate-400 uppercase tracking-wide mb-1.5">Payment method</p>
              {paymentMethod?.has_payment_method && paymentMethod?.card ? (
                <>
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-teal-600" />
                    <span className="text-sm text-slate-800 font-medium capitalize">{paymentMethod.card.brand || 'Card'} •••• {paymentMethod.card.last4 || '****'}</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">Exp {paymentMethod.card.exp_month || '--'}/{paymentMethod.card.exp_year || '----'}</p>
                </>
              ) : (
                <p className="text-sm text-slate-500">None on file</p>
              )}
            </div>
          </div>
        )}

        {hasActivePlan && (
          <div className="px-5 py-3 border-t border-slate-100">
            <Button
              onClick={handleCancelSubscription}
              disabled={actionLoading}
              variant="ghost"
              size="sm"
              className="h-8 text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 px-2"
              data-testid="cancel-subscription-btn"
            >
              Cancel subscription
            </Button>
          </div>
        )}
      </div>

      {/* Plan could not be loaded — never show the re-subscribe grid in this state */}
      {planError && !hasActivePlan && (
        <div className="bg-white border border-amber-200 rounded-xl shadow-sm p-5 flex items-start gap-3" data-testid="plan-load-error">
          <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-semibold text-slate-800">Couldn't load your plan</p>
            <p className="text-xs text-slate-500 mt-0.5">We couldn't confirm your current subscription. Please retry — don't start a new plan until this loads.</p>
            <Button onClick={fetchBillingData} size="sm" variant="outline" className="h-8 text-xs mt-3">Retry</Button>
          </div>
        </div>
      )}

      {/* Plan selection grid (confirmed no active plan) */}
      {!hasActivePlan && !planError && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {PLAN_CARDS.map((p) => (
              <div
                key={p.key}
                className={`relative bg-white rounded-xl border p-5 flex flex-col transition-shadow hover:shadow-md ${p.popular ? 'border-teal-300 ring-1 ring-teal-200' : 'border-slate-200'}`}
              >
                {p.popular && (
                  <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-teal-600 text-white text-[10px] font-semibold uppercase tracking-wide">Most popular</span>
                )}
                <h3 className="text-sm font-bold text-slate-900">{p.name}</h3>
                <p className="text-[11px] text-slate-500">{p.tagline}</p>
                <p className="text-2xl font-bold text-slate-900 mt-3">{monthlyLabel(p.key).split(' ')[0]}<span className="text-xs font-normal text-slate-400"> /mo</span></p>
                <ul className="space-y-1.5 mt-4 mb-5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-slate-600">
                      <CheckCircle className="w-3.5 h-3.5 text-teal-600 shrink-0 mt-0.5" />{f}
                    </li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleUpgrade(p.key)}
                  disabled={actionLoading}
                  className={`w-full h-9 text-sm font-semibold ${p.popular ? 'bg-teal-600 hover:bg-teal-700 text-white' : 'bg-white border border-slate-300 text-slate-800 hover:border-teal-400 hover:text-teal-700'}`}
                  data-testid={`${p.key}-plan-btn`}
                >
                  Choose {p.name}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inline Card Form - Shows when Add Card is clicked */}
      {showCardForm && (
        <AddCardForm
          onSuccess={() => {
            setShowCardForm(false);
            fetchBillingData();
          }}
          onCancel={() => setShowCardForm(false)}
        />
      )}

      {/* Saved Cards Section - Always visible */}
      {!showCardForm && (
        <SavedCardsSection
          paymentMethods={savedCards}
          onRefresh={fetchBillingData}
          onAddCard={() => setShowCardForm(true)}
        />
      )}

      {/* Upgrade Options (if on lower tier) */}
      {hasActivePlan && currentPlan.plan !== 'enterprise' && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
          <div className="px-5 py-3 border-b border-slate-100">
            <h2 className="text-sm font-semibold text-slate-800">Upgrade your plan</h2>
          </div>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {currentPlan.plan === 'starter' && (
              <div className="border border-slate-200 rounded-xl p-4 hover:border-teal-300 transition-colors">
                <h3 className="text-sm font-semibold text-slate-900">Professional</h3>
                <p className="text-xs text-slate-500 mb-2">{monthlyLabel('professional')}</p>
                <ul className="space-y-1 mb-3">
                  <li className="text-xs text-slate-600 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-teal-600" /> 4–10 properties</li>
                  <li className="text-xs text-slate-600 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-teal-600" /> Priority support</li>
                  <li className="text-xs text-slate-600 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-teal-600" /> Team accounts</li>
                </ul>
                <Button onClick={() => handleUpgrade('professional')} size="sm" className="w-full h-8 bg-teal-600 hover:bg-teal-700 text-white">
                  Upgrade
                </Button>
              </div>
            )}
            <div className="border border-teal-200 rounded-xl p-4 bg-teal-50/40">
              <h3 className="text-sm font-semibold text-slate-900">Enterprise</h3>
              <p className="text-xs text-slate-500 mb-2">{monthlyLabel('enterprise')}</p>
              <ul className="space-y-1 mb-3">
                <li className="text-xs text-slate-600 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-teal-600" /> Unlimited properties</li>
                <li className="text-xs text-slate-600 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-teal-600" /> API access</li>
                <li className="text-xs text-slate-600 flex items-center gap-1.5"><CheckCircle className="w-3 h-3 text-teal-600" /> Bulk operations</li>
              </ul>
              <Button onClick={() => handleUpgrade('enterprise')} size="sm" className="w-full h-8 bg-teal-600 hover:bg-teal-700 text-white">
                Upgrade
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Payment History */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-800">Payment history</h2>
        </div>
        
        {paymentHistory.length === 0 ? (
          <div className="p-6 text-center">
            <CreditCard className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            <p className="text-xs text-slate-500">No payments yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-slate-500 uppercase">Date</th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-slate-500 uppercase">Description</th>
                  <th className="px-3 py-2 text-left text-[11px] font-medium text-slate-500 uppercase hidden sm:table-cell">Account ID</th>
                  <th className="px-3 py-2 text-right text-[11px] font-medium text-slate-500 uppercase">Amount</th>
                  <th className="px-3 py-2 text-center text-[11px] font-medium text-slate-500 uppercase">Receipt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paymentHistory.map((payment) => (
                  <tr key={payment.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{formatDate(payment.date)}</td>
                    <td className="px-3 py-2.5 text-slate-800 truncate max-w-[120px] sm:max-w-none">{payment.description}</td>
                    <td className="px-3 py-2.5 hidden sm:table-cell">
                      <span className="text-[11px] font-mono bg-slate-100 px-1.5 py-0.5 rounded text-slate-700">
                        {payment.account_id}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 font-medium text-slate-800 text-right whitespace-nowrap">
                      ${payment.amount.toFixed(2)}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <button 
                        onClick={() => handleViewInvoice(payment.id)}
                        className="text-teal-600 hover:text-teal-700 inline-flex items-center gap-1 text-[11px] font-medium cursor-pointer"
                        data-testid={`view-bill-${payment.id}`}
                      >
                        <FileText className="w-3 h-3" /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Success Modal */}
      <SubscriptionSuccessModal
        isOpen={showSuccessModal}
        plan={successPlan}
        onClose={() => {
          setShowSuccessModal(false);
          window.location.reload(); // Reload to update navigation
        }}
      />
    </div>
  );
}
